/**
 * The Google button on `/sign-in`.
 *
 * Google draws the button itself, inside the container below, so its wording,
 * logo, font, and padding are Google's and stay compliant without this repo
 * shipping a logo or a font.
 *
 * When Google's script will not load, this renders nothing and shows no error
 * modal. Nobody pressed anything yet, and a modal over a screen a fan has not
 * touched is the exact failure the error rules exist to stop.
 */
import {
  clampProviderButtonWidth,
  renderGoogleSignInButton,
} from "@sportsmet/auth-web";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { useSystemDark } from "../../theme/useSystemDark";

export type typeGoogleSignInButtonProps = {
  /** google.webClientId from POST /oauth/provider-config-get. Never empty. */
  webClientId: string;
  onIdToken: (args: { idToken: string }) => void;
};

export const GoogleSignInButton = ({
  webClientId,
  onIdToken,
}: typeGoogleSignInButtonProps): ReactNode => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isRenderable, setIsRenderable] = useState(true);
  const isDark = useSystemDark();

  // The parent passes a new closure on every render. Reading the callback
  // through a ref keeps it out of the render effect's dependencies, so Google
  // is not asked to redraw its button in the middle of a press.
  const onIdTokenRef = useRef(onIdToken);
  useEffect(() => {
    onIdTokenRef.current = onIdToken;
  }, [onIdToken]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const widthPx = clampProviderButtonWidth({
      containerWidthPx: container.clientWidth,
    });

    renderGoogleSignInButton({
      container,
      webClientId,
      theme: isDark ? "outline_dark" : "outline",
      widthPx,
      onIdToken: (args) => {
        onIdTokenRef.current(args);
      },
    }).catch(() => {
      setIsRenderable(false);
    });
  }, [webClientId, isDark]);

  if (!isRenderable) return null;

  // `justify-center` because Google honors `width` as a minimum and may come
  // back narrower than the container on a wide viewport.
  return <div ref={containerRef} className="flex w-full justify-center" />;
};
